// The marketplace ads currently attached to this listing.
//
// ## Why cards, and not the chip they used to be
//
// A link was one `<Chip>` labelled with its ORIGIN ALONE, so three linked yad2 ads were
// three identical "יד2" chips and detaching the right one was a coin flip. Worse, that
// single chip was simultaneously an external link (click) and the destructive detach
// (its onDelete ✕), with no confirmation and no success feedback — only a snackbar when
// it failed. Unlinking is not cosmetic: it restores the market original to her board.
//
// So: one card per link, showing the ad (thumbnail, address, price, rooms, size), its
// staleness, and TWO clearly separate actions — open the ad, or detach it.
//
// ## HYDRATION DECORATES; IT NEVER REPLACES
//
// `value` — the links as the listing stores them — is the source of truth for WHICH rows
// exist. `fetchListingsByIds` silently drops ids whose document is missing, so rendering
// from the fetch result would make a de-listed ad VANISH from this list. That is the one
// unrecoverable state: the only detach affordance is on the row, while
// `sourceListingIds` keeps hiding that market ad from her board forever. A link whose
// document is gone therefore renders as an explicit "no longer on the marketplace" card,
// still detachable. Same shape CalendarListingCard uses for a deleted listing.
//
// Point reads, not a query: firestore.rules grants `get` on yad2-listings-latest and
// DENIES `list`, so `where(documentId(),'in',ids)` fails at runtime.
//
// ## "Delisted" is a VERDICT, and only a finished fetch may write it
//
// The first version derived it as `!hit && !loading`, with `loading` starting false and
// only turned on INSIDE the effect — which React runs after paint. So the very first
// frame of every healthy link painted the orange "no longer on the marketplace" card,
// then a skeleton, then the ad. The one state this component is careful never to reach
// incorrectly was the one it showed first, every time.
//
// Each id therefore carries an explicit probe verdict (`pending` → `missing` | `failed`)
// and the warning renders for `missing` alone. `failed` is a separate, non-alarming
// state on purpose: a read that threw says nothing about whether the ad still exists,
// and painting "removed from the marketplace" over a network blip is the same lie in
// slower motion.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { LinkOff, OpenInNew, ReportProblemOutlined } from "@material-ui/icons";
import { CondensedListingRowContent } from "components/_dashboard/signatures/NewListingPicker";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import type { FirestoreListingHit } from "utils/listings/fetchListings";
import { STALENESS_LABEL_KEY, listingStaleness } from "./sourceListingStaleness";
import type { ListingOrigin, ListingSourceRef } from "../../../../@types/sharedSchema";

const ORIGIN_LABEL: Record<string, string> = { yad2: "יד2", madlan: "מדלן", webot: "Webot" };

/**
 * What this session learned about a link the form does not know yet.
 *
 * `NewListingDialog` rebuilds `sourceListings` from a bare id list and fabricates
 * `{ origin: 'yad2' }` with no url for anything it has not seen before — so a freshly
 * linked madlan ad rendered as "יד2", with no way to open it, until the page reloaded.
 * The picker knows better (it just linked a candidate whose origin and url it holds), and
 * this is how that knowledge reaches the card without the form having to carry it.
 */
export type SourceLinkHint = { origin?: ListingOrigin; url?: string };

/**
 * What a hydration pass has established about ONE link, when `hydrated` has no document
 * for it.
 *
 * `pending` is the initial value for every id and is the ONLY state the component can be
 * in before a fetch resolves — which is what makes the "no longer on the marketplace"
 * card unreachable until there is an answer to render it from.
 */
type ProbeState = "pending" | "missing" | "failed";

type LinkedSourceCardsProps = {
  value: ListingSourceRef<unknown>[];
  hints: Record<string, SourceLinkHint>;
  disabled?: boolean;
  busyId: string | null;
  onUnlink: (sourceListingId: string) => void;
};

/**
 * `updatedTime`, whatever shape it arrived in.
 *
 * `GenericConverter` turns Firestore Timestamps into JS Dates on the way out, so a Date
 * is what this normally sees — but the `toDate()` branch is kept because a raw Timestamp
 * reaching here would otherwise fall through to `null`, which reads as "fresh" and would
 * silently stop the staleness label from ever appearing.
 */
const epochMs = (value: unknown): number | null => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" && isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return isFinite(parsed) ? parsed : null;
  }
  if (value && typeof (value as { toDate?: unknown }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate().getTime();
  }
  return null;
};

export default function LinkedSourceCards({
  value,
  hints,
  disabled,
  busyId,
  onUnlink
}: LinkedSourceCardsProps): JSX.Element | null {
  const { t: translate } = useTranslation();
  const [hydrated, setHydrated] = useState<Record<string, FirestoreListingHit>>({});
  const [probe, setProbe] = useState<Record<string, ProbeState>>({});

  const ids = value.map((ref) => ref.listingId);
  const idsKey = ids.join("|");

  useEffect(() => {
    const wanted = idsKey ? idsKey.split("|") : [];
    if (wanted.length === 0) {
      setHydrated({});
      setProbe({});
      return;
    }
    let cancelled = false;

    // Rebuilt from `wanted` rather than merged, so a link she detached stops being
    // tracked. An id already carrying a verdict keeps it — re-probing a known-missing
    // ad should not flicker its card back to a skeleton.
    setProbe((prev) => {
      const next: Record<string, ProbeState> = {};
      wanted.forEach((id) => {
        next[id] = prev[id] ?? "pending";
      });
      return next;
    });

    fetchListingsByIds(wanted)
      .then((hits) => {
        if (cancelled) return;
        // Keyed by id and MERGED over what we already had, never assigned wholesale: a
        // transient read failure must not demote a card that was rendering fine to the
        // "no longer on the marketplace" state.
        setHydrated((prev) => {
          const next = { ...prev };
          hits.forEach((hit) => {
            next[hit.objectID] = hit;
          });
          return next;
        });
        // The pass finished, so every id it asked about now has an answer: found (the
        // hydrated document renders and the verdict is irrelevant) or genuinely absent.
        const found = new Set(hits.map((hit) => hit.objectID));
        setProbe((prev) => {
          const next = { ...prev };
          wanted.forEach((id) => {
            next[id] = found.has(id) ? "pending" : "missing";
          });
          return next;
        });
      })
      .catch((error) => {
        console.error("LinkedSourceCards: failed to load linked marketplace ads", error);
        if (cancelled) return;
        // A throw is evidence about the READ, not about the ad. Anything already
        // hydrated keeps rendering; the rest says so plainly instead of claiming the
        // marketplace dropped it.
        setProbe((prev) => {
          const next = { ...prev };
          wanted.forEach((id) => {
            if (next[id] !== "missing") next[id] = "failed";
          });
          return next;
        });
      });

    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  if (value.length === 0) return null;

  return (
    <Stack spacing={1}>
      {value.map((ref) => {
        const hit = hydrated[ref.listingId];
        const hint = hints[ref.listingId] ?? {};
        const origin = hit?.listingOrigin ?? hint.origin ?? ref.origin;
        const url = hit?.originalListingUrl ?? hint.url ?? ref.url;
        const busy = busyId === ref.listingId;
        // A hydrated document always wins; otherwise the verdict decides, and an id with
        // no verdict yet is still pending by construction.
        const state: "hit" | ProbeState = hit ? "hit" : probe[ref.listingId] ?? "pending";
        const missing = state === "missing";
        const staleness = hit ? listingStaleness(origin, epochMs(hit.updatedTime)) : "fresh";

        return (
          <Box
            key={ref.listingId}
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: "1px solid",
              borderColor: missing ? "warning.light" : "divider",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              gap: 1.25
            }}
          >
            {hit ? (
              // The linked ad IS a full listing document, so the app's own condensed row
              // renders it — same thumbnail, address and meta line as the signature
              // picker and the favourites modal.
              <CondensedListingRowContent listing={hit} translate={translate} />
            ) : state === "pending" ? (
              <>
                <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: 1, flexShrink: 0 }} />
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="35%" />
                </Stack>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: 1,
                    bgcolor: "background.neutral",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <ReportProblemOutlined
                    sx={{ color: missing ? "warning.main" : "text.disabled", fontSize: 22 }}
                  />
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                  <Stack direction="row" alignItems="center" sx={{ gap: 0.75 }}>
                    <Chip size="small" label={ORIGIN_LABEL[origin] ?? origin} />
                    <Typography variant="subtitle2" noWrap>
                      {translate(
                        missing
                          ? "listingsPage.sourcePicker.sourceDelisted"
                          : "listingsPage.sourcePicker.sourceLoadFailed"
                      )}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {translate(
                      missing
                        ? "listingsPage.sourcePicker.sourceDelistedHint"
                        : "listingsPage.sourcePicker.sourceLoadFailedHint"
                    )}
                  </Typography>
                </Stack>
              </>
            )}

            <Stack sx={{ flexShrink: 0, alignItems: "flex-end", gap: 0.25 }}>
              <Stack direction="row" sx={{ gap: 0.25 }}>
                {url && (
                  <Tooltip title={translate("listingsPage.sourcePicker.openAd") as string}>
                    <IconButton size="small" component="a" href={url} target="_blank" rel="noopener">
                      <OpenInNew sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {/* A separate control from "open the ad", and never the same pixel: this
                    one detaches the link and puts the market original back on her board. */}
                <Tooltip title={translate("listingsPage.sourcePicker.unlink") as string}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled || busy}
                      onClick={() => onUnlink(ref.listingId)}
                      sx={{ "&:hover": { color: "error.main" } }}
                    >
                      {busy ? <CircularProgress size={15} /> : <LinkOff sx={{ fontSize: 17 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              {staleness !== "fresh" && (
                <Typography variant="caption" sx={{ color: "text.disabled", whiteSpace: "nowrap" }}>
                  {translate(STALENESS_LABEL_KEY[staleness])}
                </Typography>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
